import React, { useMemo, useState } from 'react';
import dynamic from 'next/dynamic';

import { useTranslation } from 'next-i18next';
import { useContextSelector } from 'use-context-selector';
import { UserContext } from '@context/user.context';
import AccountSettingsSectionHeader from '@components/account-settings/account-settings-section-header/account-settings-section-header';
import Button from '@components/atoms/button/button';
import filterGroupConfigurations from '@config/catalog/filter-group-configurations';

import styles from './my-sizes.module.scss';

const DialogWrapper = dynamic(() => import('@components/catalog/my-sizes/my-sizes/dialog-wrapper/dialog-wrapper'), {
  ssr: false,
});

const sizeToLabel: Record<string, string> = {
  '1': 'MYSIZES.SIZE_CATEGORY.SIZE1',
  '3': 'MYSIZES.SIZE_CATEGORY.SIZE3',
  '4': 'MYSIZES.SIZE_CATEGORY.SIZE4',
  '10': 'MYSIZES.SIZE_CATEGORY.SIZE10',
  '21': 'MYSIZES.SIZE_CATEGORY.SIZE21',
};

const MySizes: React.FC = () => {
  const { t } = useTranslation('account-settings');
  const { t: tCommon } = useTranslation();
  const userSizes = useContextSelector(UserContext, (v) => v.userSizes);
  const [dialogOpen, setDialogOpen] = useState(false);

  const doesUserHaveSizes = !!userSizes?.universe?.ids?.length;

  const badgesByUniverse = useMemo(() => {
    if (!userSizes?.size_categories) {
      return [];
    }

    return userSizes.universe.ids?.map((universeId) => {
      const badges = userSizes.size_categories
        .map((category) => {
          const catUniverseId =
            userSizes.universe.ids?.length === 1
              ? userSizes.universe.ids[0]
              : filterGroupConfigurations.find((x) => x.id === category.id)?.universeId;

          if (catUniverseId !== universeId) {
            return null;
          }

          const sizesLabel = category.sizes.map((size) => size.label).join(', ');
          if (!sizesLabel) {
            return null;
          }

          return {
            id: category.id,
            label: `${tCommon(sizeToLabel[category.id]) || category.label}: ${sizesLabel}`,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.label.length - b.label.length);

      const header =
        universeId === '1' ? tCommon('MYSIZES.SIZE_CATEGORY.UNIVERSE1') : tCommon('MYSIZES.SIZE_CATEGORY.UNIVERSE2');

      return { universeId, header, badges };
    });
  }, [userSizes, tCommon]);

  return (
    <section className={styles.mySizes}>
      <AccountSettingsSectionHeader
        title={t('ACCOUNT_SETTINGS.MY_SIZES.TITLE')}
        onEditClick={() => setDialogOpen(true)}
        trackingProperty="my_sizes"
        isEditable={doesUserHaveSizes}
        data-cy="account-settings-mysizes-edit-button"
      />

      {doesUserHaveSizes ? (
        <div className={styles.mySizes__content}>
          {badgesByUniverse?.map(({ universeId, header, badges }) => (
            <React.Fragment key={`mysizes_group_${universeId}`}>
              <div
                className={styles.mySizes__header}
                data-cy={`account-settings-mysizes-universe-header-${universeId}`}
              >
                {header}
              </div>
              <div className={styles.mySizes__badges}>
                {badges.map((badge) => (
                  <span
                    key={`mysizes_badge_${badge.id}`}
                    className={styles.mySizes__badge}
                    data-cy={`account-settings-mysizes-badge-${badge.id}`}
                  >
                    {badge.label}
                  </span>
                ))}
              </div>
            </React.Fragment>
          ))}
        </div>
      ) : (
        <>
          <p className={styles.mySizes__noSizes}>{t('ACCOUNT_SETTINGS.MY_SIZES.NO_SIZES')}</p>
          {!doesUserHaveSizes && (
            <Button
              type="button"
              size="medium"
              variant="rounded-secondary"
              onClick={() => setDialogOpen(true)}
              aria-label={t('ACCOUNT_SETTINGS.MY_SIZES.NO_SIZES_CTA')}
            >
              {t('ACCOUNT_SETTINGS.MY_SIZES.NO_SIZES_CTA')}
            </Button>
          )}
        </>
      )}

      {dialogOpen && (
        <DialogWrapper
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          canActivateSizes={true}
        />
      )}
    </section>
  );
};

export default MySizes;
